/** Auth + onboarding state, backed by the real backend. Cookie session with a
 * double-submit CSRF token. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '@/lib/api';

export interface OnboardingState {
  email_verified: boolean;
  consents: Record<string, boolean>;
  has_active_device: boolean;
  modalities: Record<string, boolean>;
  missing: string[];
  ready_for_activation: boolean;
}

export interface EnrolmentSummary {
  status: string;
  accepted_captures: number;
  required_captures: number;
  rejected_captures?: number;
  best_quality_score?: number | null;
  liveness_result?: string | null;
  provider_maturity?: string | null;
  synthetic?: boolean;
}

export interface Me {
  identity_id: string;
  email: string;
  display_name: string | null;
  status: string;
  email_verified: boolean;
  document_registered: boolean;
  document_validated: boolean;
  onboarding: OnboardingState;
  enrolments: Record<string, EnrolmentSummary>;
  synthetic_mode?: boolean;
}

interface AuthContextValue {
  me: Me | null | false; // null = comprobando, false = sin sesión
  refresh: () => Promise<Me | false>;
  login: (identifier: string, password: string) => Promise<Me | false>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
}

export interface RegisterPayload {
  email: string;
  password: string;
  display_name?: string;
  document_number?: string;
  document_country_code?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null | false>(null);

  const refresh = useCallback(async (): Promise<Me | false> => {
    try {
      const next = await apiGet<Me>('/api/v1/me');
      setMe(next);
      return next;
    } catch {
      setMe(false);
      return false;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      await apiPost('/api/v1/auth/login', { identifier, password });
      return refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      await apiPost('/api/v1/auth/register', payload);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/v1/auth/logout', {});
    } finally {
      setMe(false);
    }
  }, []);

  const value = useMemo(
    () => ({ me, refresh, login, register, logout }),
    [me, refresh, login, register, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}

/** Spanish, non-technical copy for every backend error code. */
export const ERROR_COPY_ES: Record<string, string> = {
  VALIDATION_ERROR: 'Revisa los datos ingresados: algo no es válido.',
  CONFLICT: 'Ese dato ya está registrado en Tcheck.',
  UNAUTHENTICATED: 'Necesitas iniciar sesión para continuar.',
  FORBIDDEN: 'No tienes permisos para esta acción.',
  NOT_FOUND: 'No encontramos lo que buscabas.',
  RATE_LIMITED: 'Demasiados intentos. Espera unos minutos antes de reintentar.',
  SERVICE_UNAVAILABLE: 'El servicio no está disponible en este momento.',
  PROVIDER_UNAVAILABLE: 'El motor biométrico no está disponible. Intenta más tarde.',
  BIOMETRIC_NO_FACE_DETECTED: 'No detectamos tu rostro. Céntrate frente a la cámara.',
  BIOMETRIC_MULTIPLE_FACES: 'Hay más de una persona en la imagen. Quédate solo frente a la cámara.',
  BIOMETRIC_QUALITY_TOO_LOW: 'La captura no tiene calidad suficiente. Busca mejor luz.',
  BIOMETRIC_NO_HAND_DETECTED: 'No detectamos tu palma. Muestra la mano abierta.',
  LIVENESS_FAILED: 'No pudimos confirmar que estés presente. Repite la prueba.',
  LIVENESS_INCONCLUSIVE: 'La prueba de vida fue inconclusa. Vuelve a intentarlo.',
  VOICE_TRANSCRIPTION_FAILED: 'No pudimos procesar el audio. Graba de nuevo, más cerca del micrófono.',
  VOICE_CHALLENGE_MISMATCH: 'La frase pronunciada no coincide con la solicitada.',
  WEBAUTHN_CHALLENGE_NOT_FOUND: 'La sesión de verificación no es válida. Vuelve a empezar.',
  WEBAUTHN_CHALLENGE_EXPIRED: 'El desafío expiró. Pide uno nuevo.',
  WEBAUTHN_VERIFICATION_FAILED: 'No pudimos verificar tu dispositivo.',
  POLICY_REQUIREMENT_NOT_MET: 'Todavía faltan pasos para completar tu seguridad.',
  SYNTHETIC_NOT_ALLOWED: 'Las muestras sintéticas no están permitidas en este entorno.',
  INTERNAL_ERROR: 'Ocurrió un error inesperado. Intenta nuevamente en unos instantes.',
  NETWORK: 'Se perdió la conexión. Revisa tu internet e intenta otra vez.',
};

/** Spanish coaching for provider rejection reasons. */
export const COACHING_ES: Record<string, string> = {
  no_face_detected: 'No detectamos tu rostro. Centra tu rostro',
  multiple_faces: 'Hay más de un rostro. Quédate solo frente a la cámara',
  face_too_small_or_far: 'Acércate ligeramente',
  image_blurry: 'Mantén el teléfono quieto',
  poor_illumination: 'Mejora la iluminación',
  head_pose_off_axis: 'Mira al frente',
  no_hand_detected: 'No detectamos la palma. Muestra la mano abierta',
  hand_too_far: 'Acerca la mano a la cámara',
  palm_not_facing_camera: 'Gira la palma hacia la cámara',
  insufficient_audio: 'El audio fue muy corto. Habla un poco más',
  challenge_phrase_mismatch: 'La frase no coincide con la solicitada',
};
